import { initializeStore } from "@/redux/store";
import { api } from "@/redux/api";

const TTL = 60 * 60 * 1000; // 1 hour TTL

let cachedCommonData = { data: null, timestamp: 0 };
let cachedWorldTourData = { data: null, timestamp: 0 };

const detectIsMobile = (userAgent = "") =>
    /mobile|android|iphone|ipad|phone/i.test(userAgent);

const fetchCachedCommonData = async (store, isWorldTour, params = {}) => {
    const now = Date.now();

    if (isWorldTour) {
        if (!cachedWorldTourData.data || now - cachedWorldTourData.timestamp > TTL) {
            const { data } = await store.dispatch(api.endpoints.getCommonWorldTourData.initiate(params));
            cachedWorldTourData = { data, timestamp: now };
        }
        return cachedWorldTourData.data;
    } else {
        if (!cachedCommonData.data || now - cachedCommonData.timestamp > TTL) {
            const { data } = await store.dispatch(api.endpoints.getCommonData.initiate(params));
            cachedCommonData = { data, timestamp: now };
        }
        return cachedCommonData.data;
    }
};

export const fetchPageData = async (endpoint, context) => {
    const store = initializeStore();
    const userAgent = context?.req?.headers?.["user-agent"] || "";
    const isMobile = detectIsMobile(userAgent);

    const { data: pageResponse } = await store.dispatch(api.endpoints[endpoint].initiate());
    const pageData = pageResponse?.data || {};
    const isWorldTour = pageData.isWorldTour;

    const commonDataResponse = await fetchCachedCommonData(store, isWorldTour);

    const mergedData = {
        ...commonDataResponse,
        ...pageResponse,
        data: {
            ...commonDataResponse?.data,
            ...pageData,
        },
    };

    return {
        props: {
            data: mergedData || null,
            metadata: {
                title: pageResponse?.meta?.title ?? "Default Title",
                description: pageResponse?.meta?.description ?? "Default Description",
                image: pageResponse?.meta?.image?.url ?? null,
            },
            isMobile,
        },
    };
};

export const fetchStaticProps = async (endpoint, params, context) => {
    const store = initializeStore();
    const userAgent = context?.req?.headers?.["user-agent"] || "";
    const isMobile = detectIsMobile(userAgent);

    try {
        const { data: pageResponse } = await store.dispatch(api.endpoints[endpoint].initiate(params));
        const pageData = pageResponse?.data || {};

        if (!pageData) {
            return { notFound: true };
        }

        const commonDataResponse = await fetchCachedCommonData(store, pageData.isWorldTour, params);

        const mergedData = {
            ...commonDataResponse,
            ...pageResponse,
            data: {
                ...commonDataResponse?.data,
                ...pageData,
            },
        };

        return {
            props: {
                data: mergedData || null,
                metadata: {
                    title: pageResponse?.meta?.title ?? "Default Title",
                    description: pageResponse?.meta?.description ?? "Default Description",
                    image: pageResponse?.meta?.image?.url ?? null,
                },
                structuredData: pageData?.page?.structuredData ?? null,
                isMobile,
            },
        };
    } catch (error) {
        if (error?.status === 404) {
            return { notFound: true };
        }

        return {
            props: {
                error: "An unexpected error occurred.",
            },
        };
    }
};

export const fetchStaticPaths = async (endpoint, mapCallback) => {
    const store = initializeStore();
    const { data } = await store.dispatch(api.endpoints[endpoint].initiate());

    const paths = data?.data?.map(mapCallback) || [];

    return {
        paths,
        fallback: "blocking",
    };
};
